import React, {useState, useEffect, useMemo, useRef} from 'react';
import {RecyclingChart} from "@/pages/Recycle/recycling-chart";
import TreesEquivalent from './TreesEquivalent';
import RecyclabilityScore from './RecyclabilityScore';
import WasteDiverted from './WasteDiverted';
import {WasteImpactCard} from './WasteImpact.jsx';
import CO2SavingsChart from '../CO2savings/co2';
import axiosInstance from '@/api/axios';
import {GHGAvertedCard} from './GHGaverted';
import {RecyclingHeader} from './siteheader';

// --- localStorage persistence helpers -------------------------------------
const LS_KEYS = {
    year: 'recyclingDashboard.year',
    bins: 'recyclingDashboard.selectedBins',
    wasteType: 'recyclingDashboard.selectedWasteType',
    period: 'recyclingDashboard.selectedTimePeriod',
    dateRange: 'recyclingDashboard.customDateRange',
    isCustomRange: 'recyclingDashboard.isCustomDateRange',
};

function readLS(key, fallback) {
    if (typeof window === 'undefined') return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function writeLS(key, value) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // storage full/disabled — fail silently
    }
}
// ---------------------------------------------------------------------------

export default function RecyclingDashboard() {
    // Lazy initializers: read from localStorage once on first render instead
    // of hardcoded defaults, so a refresh restores whatever was last picked.
    const [year, setYear] = useState(() => readLS(LS_KEYS.year, new Date().getFullYear()));
    const [selectedBins, setSelectedBins] = useState(() => readLS(LS_KEYS.bins, []));
    const [selectedWasteType, setSelectedWasteType] = useState(() => readLS(LS_KEYS.wasteType, 'organic'));
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(() => readLS(LS_KEYS.period, 'year'));
    const [isFetching, setIsFetching] = useState(false);
    const [apiData, setApiData] = useState(null);
    const [wasteDivertedData, setWasteDivertedData] = useState(null);
    const [environmentalImpactData, setEnvironmentalImpactData] = useState(null);
    const [availableBins, setAvailableBins] = useState([]);

    // Custom date range states — also restored from localStorage.
    const [customDateRange, setCustomDateRange] = useState(() =>
        readLS(LS_KEYS.dateRange, [{ startDate: null, endDate: null, key: 'selection' }])
    );
    const [isCustomDateRange, setIsCustomDateRange] = useState(() => readLS(LS_KEYS.isCustomRange, false));

    // Persist each piece of state whenever it changes.
    useEffect(() => { writeLS(LS_KEYS.year, year); }, [year]);
    useEffect(() => { writeLS(LS_KEYS.bins, selectedBins); }, [selectedBins]);
    useEffect(() => { writeLS(LS_KEYS.wasteType, selectedWasteType); }, [selectedWasteType]);
    useEffect(() => { writeLS(LS_KEYS.period, selectedTimePeriod); }, [selectedTimePeriod]);
    useEffect(() => { writeLS(LS_KEYS.dateRange, customDateRange); }, [customDateRange]);
    useEffect(() => { writeLS(LS_KEYS.isCustomRange, isCustomDateRange); }, [isCustomDateRange]);

    // Stable serialized key for useEffect dependency — avoids object reference issues
    const customDateRangeKey = useMemo(() => {
        if (!isCustomDateRange) return null;
        const r = customDateRange[0];
        if (!r?.startDate || !r?.endDate) return null;
        const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        const end = r.endDate instanceof Date ? r.endDate.toISOString() : r.endDate;
        return `${start}__${end}`;
    }, [customDateRange, isCustomDateRange]);

    // Keep a live ref of selectedBins so the bin-id fetch effect below never
    // reads a stale closure value — this was the actual bug: with an empty
    // dependency array, `selectedBins.length === 0` always evaluated against
    // the initial-render value, so a restored selection got silently
    // overwritten as soon as the bin_ids call resolved.
    const selectedBinsRef = useRef(selectedBins);
    useEffect(() => { selectedBinsRef.current = selectedBins; }, [selectedBins]);

    // Fetch bin IDs dynamically
    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                setAvailableBins(binIds);
                // Only fall back to auto-selecting the first bin if there's
                // truly nothing selected (nothing restored from storage and
                // nothing picked yet) — checked against the live ref, not a
                // stale closure value.
                if (binIds.length > 0 && selectedBinsRef.current.length === 0) {
                    setSelectedBins([binIds[0]]);
                }
            } catch (error) {
                console.error("RecyclingDashboard: Error fetching bin IDs:", error);
            }
        };
        fetchBinIds();
    }, []);

    const handleCustomDateRangeChange = (dateRange, isCustom) => {
        setCustomDateRange(dateRange);
        setIsCustomDateRange(isCustom);
        if (isCustom) {
            setSelectedTimePeriod('custom');
        }
    };

    // Build shared request params — single source of truth
    const buildParams = (extraParams = {}) => {
        const params = {
            'bins[]': selectedBins,
            ...extraParams
        };
        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
            const r = customDateRange[0];
            params.start_date = r.startDate instanceof Date
                ? r.startDate.toISOString()
                : r.startDate;
            params.end_date = r.endDate instanceof Date
                ? r.endDate.toISOString()
                : r.endDate;
        } else {
            params.period = selectedTimePeriod;
            if (selectedTimePeriod === 'year') {
                params.year = year;
            }
        }
        return params;
    };

    const fetchRecyclingData = async () => {
        if (selectedBins.length === 0) return;
        setIsFetching(true);
        try {
            const response = await axiosInstance.get('recycling_rate', { params: buildParams() });
            setApiData(response.data);
        } catch (error) {
            console.error("Error fetching recycling data:", error);
            setApiData({
                recycling_rate: 0,
                historical_data: [],
                period: selectedTimePeriod,
                year
            });
        } finally {
            setIsFetching(false);
        }
    };

    const fetchWasteDivertedData = async () => {
        if (selectedBins.length === 0) return;
        try {
            // waste-diverted endpoint uses URLSearchParams (arrays need append)
            const urlParams = new URLSearchParams();
            selectedBins.forEach(bin => urlParams.append('bins[]', bin));
            if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
                const r = customDateRange[0];
                urlParams.append('start_date', r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate);
                urlParams.append('end_date', r.endDate instanceof Date ? r.endDate.toISOString() : r.endDate);
            } else {
                urlParams.append('period', selectedTimePeriod);
                if (selectedTimePeriod === 'year') urlParams.append('year', year);
            }
            const response = await axiosInstance.get('waste-diverted', { params: urlParams });
            setWasteDivertedData(response.data);
        } catch (error) {
            console.error("Error fetching waste diverted data:", error);
            setWasteDivertedData({
                waste_diverted: 0,
                total_waste: 0,
                historical_data: [],
                period: selectedTimePeriod,
                start_date: new Date().toISOString(),
                end_date: new Date().toISOString(),
                change_rate: 0
            });
        }
    };

    const fetchEnvironmentalImpactData = async () => {
        if (selectedBins.length === 0) return;
        try {
            const response = await axiosInstance.get('enviromental_impact', { params: buildParams() });
            setEnvironmentalImpactData(response.data);
        } catch (error) {
            console.error("Error fetching environmental impact data:", error);
            setEnvironmentalImpactData({
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            });
        }
    };

    // Re-fetch whenever bins, period, year, or custom date range changes.
    // customDateRangeKey is a stable string derived from the date range, so it
    // correctly triggers the effect when the user picks a new date range.
    useEffect(() => {
        fetchRecyclingData();
        fetchWasteDivertedData();
        fetchEnvironmentalImpactData();
    }, [year, selectedBins, selectedTimePeriod, customDateRangeKey]);

    const dashboardData = useMemo(() => {
        const defaultData = {
            recycling_rate: 0,
            historical_data: [],
            period: selectedTimePeriod,
            year
        };
        const data = apiData || defaultData;
        const recyclingRate = data.recycling_rate || 0;
        return {
            treesEquivalent: (recyclingRate * 0.5).toFixed(1),
            recyclabilityScore: 0,
            wasteDiverted: {
                amount: (recyclingRate * 100).toFixed(0),
                percentage: recyclingRate.toFixed(1)
            },
            recyclingRate,
            historicalData: data.historical_data || [],
            period: data.period,
            year: data.year,
            environmentalImpact: environmentalImpactData || {
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            }
        };
    }, [apiData, environmentalImpactData, year, selectedTimePeriod]);

    return (
        <>
            <RecyclingHeader
                selectedBins={selectedBins}
                setSelectedBins={setSelectedBins}
                selectedWasteType={selectedWasteType}
                setSelectedWasteType={setSelectedWasteType}
                selectedTimePeriod={selectedTimePeriod}
                setSelectedTimePeriod={setSelectedTimePeriod}
                year={year}
                setYear={setYear}
                allBins={availableBins}
                customDateRange={customDateRange}
                onCustomDateRangeChange={handleCustomDateRangeChange}
                isCustomDateRange={isCustomDateRange}
            />
            <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">
                <div className="space-y-6 pb-8 pt-4">
                    {isFetching || selectedBins.length === 0 ? (
                        <div className="flex items-center justify-center h-64">
                            <p>Loading data...</p>
                        </div>
                    ) : (
                        <>
                            {/* First Row - 4 charts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-4 sm:pt-0 items-start">
                                <div className="min-h-[200px]">
                                    <RecyclingChart data={{
                                        recycling_rate: dashboardData.recyclingRate,
                                        historical_data: dashboardData.historicalData,
                                        period: dashboardData.period,
                                        year: dashboardData.year
                                    }}/>
                                </div>

                                <div className="min-h-[200px]">
                                    <TreesEquivalent
                                        selectedBins={selectedBins}
                                        selectedPeriod={selectedTimePeriod}
                                        selectedYear={year}
                                        customDateRange={isCustomDateRange ? customDateRange : null}
                                    />
                                </div>

                                <div className="min-h-[200px]">
                                    <RecyclabilityScore
                                        year={dashboardData.year}
                                        bins={selectedBins}
                                        period={dashboardData.period}
                                        customDateRange={isCustomDateRange ? customDateRange : null}
                                    />
                                </div>

                                <div className="min-h-[200px] md:col-span-1">
                                    <WasteDiverted
                                        selectedBins={selectedBins}
                                        selectedTimePeriod={selectedTimePeriod}
                                        year={year}
                                        data={wasteDivertedData}
                                        customDateRange={isCustomDateRange ? customDateRange : null}
                                    />
                                </div>
                            </div>

                            {/* Second Row - Waste Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-4 sm:pt-0 items-start">
                                <div className="w-full">
                                    <WasteImpactCard
                                        bins={selectedBins}
                                        period={selectedTimePeriod}
                                        year={year}
                                        customDateRange={isCustomDateRange ? customDateRange : null}
                                    />
                                </div>

                                <div className="w-full">
                                    <CO2SavingsChart
                                        id={selectedBins[0] || 'R4'}
                                        wasteitem={selectedWasteType}
                                        dayvalue={selectedTimePeriod === 'year' ? year : selectedTimePeriod}
                                        selectedButton_={selectedTimePeriod === 'custom' ? 'custom' : 'day'}
                                        datevalue={isCustomDateRange ? customDateRange : [{startDate: null, endDate: null, key: 'selection'}]}
                                        co2Data={dashboardData.environmentalImpact}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}